/**
 * shadcn-style Combobox primitives on {@link https://base-ui.com/react/components/combobox Base UI},
 * paired with {@link ./input-group}.
 * https://ui.shadcn.com/docs/components/combobox (groups, filter, inline trigger).
 */
import { Combobox as ComboboxPrimitive } from "@base-ui/react";
import { CaretDownIcon, CheckIcon, XIcon } from "@phosphor-icons/react";
import {
  InputGroup,
  InputGroupAddon,
  InputGroupButton,
  InputGroupInput,
} from "@/components/ui/input-group";
import { useOverlayPortalContainer } from "@/components/ui/overlay-portal-container";
import { cn } from "@/lib/utils";

const Combobox = ComboboxPrimitive.Root;

const ComboboxTrigger = ({
  className,
  children,
  ...props
}: ComboboxPrimitive.Trigger.Props) => {
  return (
    <ComboboxPrimitive.Trigger
      data-slot="combobox-trigger"
      className={cn(
        "inline-flex size-6 shrink-0 cursor-pointer items-center justify-center rounded-md text-slate-500",
        "hover:bg-gray-100 data-pressed:bg-transparent",
        "disabled:pointer-events-none disabled:opacity-50",
        "[&_svg:not([class*='size-'])]:size-4",
        className,
      )}
      {...props}
    >
      {children}
      <CaretDownIcon
        data-slot="combobox-trigger-icon"
        className="pointer-events-none size-4 text-slate-500"
        aria-hidden
      />
    </ComboboxPrimitive.Trigger>
  );
};

const ComboboxClear = ({
  className,
  ...props
}: ComboboxPrimitive.Clear.Props) => {
  return (
    <ComboboxPrimitive.Clear
      data-slot="combobox-clear"
      render={<InputGroupButton variant="ghost" inputGroupSize="icon-xs" />}
      className={cn(className)}
      {...props}
    >
      <XIcon className="pointer-events-none size-4" aria-hidden />
    </ComboboxPrimitive.Clear>
  );
};

interface ComboboxInputProps extends ComboboxPrimitive.Input.Props {
  showTrigger?: boolean;
  showClear?: boolean;
}

const ComboboxInput = ({
  className,
  children,
  disabled,
  showTrigger = true,
  showClear = false,
  ...props
}: ComboboxInputProps) => {
  return (
    <InputGroup
      className={cn(
        "w-auto cursor-pointer has-disabled:cursor-not-allowed",
        className,
      )}
    >
      <ComboboxPrimitive.Input
        render={<InputGroupInput />}
        disabled={disabled}
        {...props}
      />
      <InputGroupAddon align="inline-end">
        {showTrigger ? (
          <ComboboxTrigger
            disabled={disabled}
            data-slot="input-group-button"
            className="group-has-data-[slot=combobox-clear]/input-group:hidden"
          />
        ) : null}
        {showClear ? <ComboboxClear disabled={disabled} /> : null}
      </InputGroupAddon>
      {children}
    </InputGroup>
  );
};

type ComboboxContentProps = ComboboxPrimitive.Popup.Props &
  Pick<
    ComboboxPrimitive.Positioner.Props,
    "side" | "align" | "sideOffset" | "alignOffset" | "anchor"
  >;

const ComboboxContent = ({
  className,
  side = "bottom",
  sideOffset = 6,
  align = "start",
  alignOffset = 0,
  anchor,
  ...props
}: ComboboxContentProps) => {
  const portalContainer = useOverlayPortalContainer();

  return (
    <ComboboxPrimitive.Portal container={portalContainer?.current ?? undefined}>
      <ComboboxPrimitive.Positioner
        side={side}
        sideOffset={sideOffset}
        align={align}
        alignOffset={alignOffset}
        anchor={anchor}
        className="isolate z-50 pointer-events-auto"
      >
        <ComboboxPrimitive.Popup
          data-slot="combobox-content"
          data-chips={!!anchor}
          className={cn(
            "group/combobox-content relative max-h-96 w-(--anchor-width) max-w-(--available-width) min-w-[calc(var(--anchor-width)+--spacing(7))] origin-(--transform-origin) overflow-hidden rounded-md bg-popover text-popover-foreground shadow-md ring-1 ring-slate-200/80 duration-100",
            "data-[chips=true]:min-w-(--anchor-width)",
            "data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2",
            "data-open:animate-in data-open:fade-in-0 data-open:zoom-in-95 data-closed:animate-out data-closed:fade-out-0 data-closed:zoom-out-95",
            "*:data-[slot=input-group]:m-1 *:data-[slot=input-group]:mb-0 *:data-[slot=input-group]:h-8 *:data-[slot=input-group]:border-slate-200/80 *:data-[slot=input-group]:bg-slate-50 *:data-[slot=input-group]:shadow-none",
            className,
          )}
          {...props}
        />
      </ComboboxPrimitive.Positioner>
    </ComboboxPrimitive.Portal>
  );
};

const ComboboxList = ({
  className,
  ...props
}: ComboboxPrimitive.List.Props) => {
  return (
    <ComboboxPrimitive.List
      data-slot="combobox-list"
      className={cn(
        "max-h-[min(calc(--spacing(96)---spacing(9)),calc(var(--available-height)---spacing(9)))] scroll-py-1 overflow-y-auto p-1 data-empty:p-0",
        "scrollbar-thin scrollbar-thumb-slate-200 scrollbar-track-transparent hover:scrollbar-thumb-slate-300",
        className,
      )}
      {...props}
    />
  );
};

const ComboboxItem = ({
  className,
  children,
  ...props
}: ComboboxPrimitive.Item.Props) => {
  return (
    <ComboboxPrimitive.Item
      data-slot="combobox-item"
      className={cn(
        "relative flex w-full cursor-pointer items-center gap-2 rounded-xs py-1.5 pr-8 pl-2 text-xs outline-hidden select-none",
        "data-highlighted:bg-slate-100 data-highlighted:text-slate-900",
        "dark:data-highlighted:bg-slate-800 dark:data-highlighted:text-slate-50",
        "data-disabled:pointer-events-none data-disabled:opacity-50",
        "[&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-4",
        className,
      )}
      {...props}
    >
      {children}
      <ComboboxPrimitive.ItemIndicator
        data-slot="combobox-item-indicator"
        render={
          <span className="pointer-events-none absolute right-2 flex size-4 items-center justify-center" />
        }
      >
        <CheckIcon className="pointer-events-none size-4" aria-hidden />
      </ComboboxPrimitive.ItemIndicator>
    </ComboboxPrimitive.Item>
  );
};

const ComboboxGroup = ({
  className,
  ...props
}: ComboboxPrimitive.Group.Props) => {
  return (
    <ComboboxPrimitive.Group
      data-slot="combobox-group"
      className={cn(className)}
      {...props}
    />
  );
};

const ComboboxLabel = ({
  className,
  ...props
}: ComboboxPrimitive.GroupLabel.Props) => {
  return (
    <ComboboxPrimitive.GroupLabel
      data-slot="combobox-label"
      className={cn(
        "px-2 py-1.5 text-xs font-medium text-muted-foreground",
        className,
      )}
      {...props}
    />
  );
};

const ComboboxCollection = ({
  ...props
}: ComboboxPrimitive.Collection.Props) => {
  return (
    <ComboboxPrimitive.Collection data-slot="combobox-collection" {...props} />
  );
};

const ComboboxEmpty = ({
  className,
  ...props
}: ComboboxPrimitive.Empty.Props) => {
  return (
    <ComboboxPrimitive.Empty
      data-slot="combobox-empty"
      className={cn(
        "hidden w-full justify-center py-2 text-center text-sm text-muted-foreground group-data-empty/combobox-content:flex",
        className,
      )}
      {...props}
    />
  );
};

const ComboboxSeparator = ({
  className,
  ...props
}: ComboboxPrimitive.Separator.Props) => {
  return (
    <ComboboxPrimitive.Separator
      data-slot="combobox-separator"
      className={cn("-mx-1 my-1 h-px bg-border", className)}
      {...props}
    />
  );
};

export {
  Combobox,
  ComboboxCollection,
  ComboboxContent,
  ComboboxEmpty,
  ComboboxGroup,
  ComboboxInput,
  ComboboxItem,
  ComboboxLabel,
  ComboboxList,
  ComboboxSeparator,
};
